import React from 'react';
import {
  FormLabel,
  Grid,
  Button,
  Stack,
  Box,
  CircularProgress,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  Modal
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// A hint of what the file is, at a glance — the upload accepts images, PDFs and text.
const FileTypeIcon = ({ name }) => {
  const ext = String(name || '')
    .split('.')
    .pop()
    .toLowerCase();
  const sx = { flexShrink: 0, color: 'text.secondary' };
  if (ext === 'pdf') return <PictureAsPdfIcon fontSize="small" sx={sx} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext))
    return <ImageIcon fontSize="small" sx={sx} />;
  return <DescriptionIcon fontSize="small" sx={sx} />;
};

import CustomDialog from '../dialog/CustomDialog';
import ModalButtons from '../button/ModalButtons';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

export default function UploadFileList({
  value,
  onChange,
  familyId,
  readOnly,
  onUploadComplete = () => {}
}) {
  // Signed URLs keyed by FILENAME, not by index.
  //
  // This used to be an array indexed positionally, refreshed by a mount-only effect, and that is why
  // clicking an attachment did nothing:
  //   * the effect had `[]` deps, so a file uploaded after mount never got a URL;
  //   * `setFileLinks(links)` was called with the SAME array it had just mutated, so React compared it
  //     equal and skipped the re-render;
  //   * deleting an attachment shifted `value` but not the links, so every chip after the deleted one
  //     pointed at the wrong file — or at nothing.
  // Keying by filename makes the mapping independent of position, and refetching when the list changes
  // makes it independent of mount order.
  const [fileLinks, setFileLinks] = React.useState({});
  const [pending, setPending] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);

  const defaultValues = { filePath: '', linkTitle: '' };
  const [formValues, setFormValues] = React.useState(defaultValues || {});

  const handleChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    });
  };

  const handleBrowse = (e) => {
    setFormValues({
      ...formValues,
      filePath: e.target?.files[0]?.name
    });
  };

  // The stored object key. Both sides of the API derive it the same way — the upload route also does
  // `.slice(0, 9)` — so this must stay in step with it.
  const storageKey = (name) =>
    familyId ? `${familyId.slice(0, 9)}/${name}` : name;

  const handleUpload = async (e) => {
    if (e.target.files.length !== 0) {
      const formData = new FormData();
      formData.append('file_attachment', e.target.files[0]);
      formData.append('fileName', familyId ? familyId : '');
      const res = await axios.post(APICONSTANTS.fileAttachment, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const name = e.target.files[0].name;
      // A NEW object, so React actually re-renders and the row becomes clickable straight away
      // instead of only after some unrelated update.
      if (res?.data?.fileURL) {
        setFileLinks((prev) => ({ ...prev, [name]: res.data.fileURL }));
      }
      if (typeof onUploadComplete === 'function') {
        onUploadComplete(res?.data || {});
      }
      return res?.data;
    }
    return null;
  };

  // Resolve a signed URL for anything in the list that does not have one yet. Depends on the list, so
  // an attachment added or removed after mount is handled — the old mount-only effect is what left
  // later uploads with no URL at all.
  const attachmentKey = (value || []).join('|');
  React.useEffect(() => {
    let cancelled = false;
    const missing = (value || []).filter((name) => name && !fileLinks[name]);
    if (!missing.length) return undefined;
    setPending(true);
    (async () => {
      const resolved = {};
      for (const name of missing) {
        try {
          const response = await axios.get(APICONSTANTS.fileAttachment, {
            params: { fileName: storageKey(name) }
          });
          if (response?.data?.fileURL) resolved[name] = response.data.fileURL;
        } catch (err) {
          // Leave it unresolved: the row then shows as unavailable rather than looking clickable and
          // doing nothing, which is the behaviour that made the original bug so hard to spot.
          void err;
        }
      }
      if (cancelled) return;
      setFileLinks((prev) => ({ ...prev, ...resolved }));
      setPending(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentKey]);

  const handleFile = async (e) => {
    const name = e.target?.files?.[0]?.name;
    if (!name) return;
    // Await the upload so the URL is in hand before the parent re-renders with the new name; the old
    // code fired these in parallel and the row could render with no link.
    await handleUpload(e);
    onChange([...(value || []), name]);
  };

  const [inputKey, setInputKey] = React.useState(Date.now());
  const handleDelete = (deletedIndex) => {
    setInputKey(Date.now());
    const deleted = (value || [])[deletedIndex];
    if (deleted == null) return;

    axios.delete(APICONSTANTS.fileAttachment, {
      params: { fileName: storageKey(deleted) }
    });

    // New array, and drop the link by NAME. Filtering the array while leaving a positional link list
    // in place is what previously made the remaining rows open the wrong file.
    onChange((value || []).filter((_, i) => i !== deletedIndex));
    setFileLinks((prev) => {
      const next = { ...prev };
      delete next[deleted];
      return next;
    });
  };

  return (
    <>
      <Grid style={{ paddingTop: '1.5rem' }} size={3}>
        <FormLabel sx={{ fontWeight: 'bold', color: 'black' }}>
          File Attachments
        </FormLabel>
      </Grid>
      <Grid size={4}></Grid>
      <Grid size={5}>
        <Button
          sx={{ float: 'right' }}
          variant="contained"
          color="secondary"
          component="label"
          disabled={readOnly}
        >
          Upload Attachments
          <input
            key={inputKey}
            accept="image/*,.pdf,.txt"
            hidden
            type="file"
            onChange={handleFile}
          />
        </Button>

        {/* A file LIST, not floating chips. A chip reads as a tag — something that classifies the
            record — whereas these are documents you open, so they get a row each with a type icon, the
            full name, and explicit open/remove actions. It also fixes two practical problems with the
            chips: a long filename was truncated with no way to see it, and there was no indication of
            whether the file was ready to open. */}
        {(value || []).length > 0 && (
          <Stack spacing={0.5} sx={{ pt: 1.5, clear: 'both' }}>
            {(value || []).map((attachment, i) => {
              const href = fileLinks[attachment];
              const unavailable = !href;
              return (
                <Box
                  key={`${attachment}-${i}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '6px',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <FileTypeIcon name={attachment} />
                  {/* The clickable element IS the anchor. Rendering a button inside a link is invalid
                      HTML and silently does not navigate — the same trap that broke "View Family
                      History" — and a real anchor also gives middle-click and open-in-new-tab. */}
                  {unavailable ? (
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                        color: 'text.disabled'
                      }}
                    >
                      {attachment}
                    </Typography>
                  ) : (
                    <Link
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                    >
                      {attachment}
                    </Link>
                  )}
                  {/* Says WHY it cannot be opened, rather than looking clickable and doing nothing. */}
                  {unavailable &&
                    (pending ? (
                      <CircularProgress size={14} sx={{ flexShrink: 0 }} />
                    ) : (
                      <Tooltip title="This file could not be found in storage">
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', flexShrink: 0 }}
                        >
                          unavailable
                        </Typography>
                      </Tooltip>
                    ))}
                  {href && (
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        component="a"
                        href={href}
                        download={attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ flexShrink: 0 }}
                        aria-label={`Download ${attachment}`}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!readOnly && (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(i)}
                        sx={{ flexShrink: 0 }}
                        aria-label={`Remove ${attachment}`}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Grid>
      <Modal
        open={modalOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CustomDialog
          open={modalOpen}
          handleClose={(e) => setModalOpen(false)}
          maxWidth={'sm'}
          minHeight={'auto'}
          title={`Add Link To File`}
        >
          <form className="user-form">
            <Grid container spacing={2}>
              <Grid size={9}>
                <Grid sx={{ paddingBlock: 1 }}>
                  <FormLabel sx={{ fontWeight: 'bold', color: 'black' }}>
                    File Path
                  </FormLabel>
                </Grid>
                <Box sx={{ display: 'flex', position: 'relative' }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    onChange={handleChange}
                    name="filePath"
                    value={formValues.filePath}
                  />
                </Box>
              </Grid>
              <Grid size={3}>
                <Grid sx={{ paddingBlock: 1 }}>
                  <FormLabel sx={{ fontWeight: 'bold', color: 'white' }}>
                    .
                  </FormLabel>
                </Grid>
                <Box
                  sx={{
                    display: 'flex',
                    position: 'relative'
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    component="label"
                    sx={{ padding: '1rem', width: '100%' }}
                    onClick={() => setModalOpen(true)}
                  >
                    Browse
                    <input
                      key={inputKey}
                      accept="image/*,.pdf,.txt"
                      hidden
                      type="file"
                      onChange={handleBrowse}
                    />
                  </Button>
                </Box>
              </Grid>
              <Grid size={12}>
                <Grid sx={{ paddingBlock: 1 }}>
                  <FormLabel sx={{ fontWeight: 'bold', color: 'black' }}>
                    Link Title
                  </FormLabel>
                </Grid>
                <Box sx={{ display: 'flex', position: 'relative' }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    onChange={handleChange}
                    name="linkTitle"
                    value={formValues.linkTitle}
                  />
                </Box>
              </Grid>
              <ModalButtons
                hideContinueAdding={true}
                intermediateBtnLabel="Delete"
                addLabel={`Add Link To File`}
                handleClose={(e) => setModalOpen(false)}
              />
            </Grid>
          </form>
        </CustomDialog>
      </Modal>
    </>
  );
}
